import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { MessageCircle, Bell, Clock, Settings, Facebook, Unlink, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://baby-app-backend.onrender.com';

const MessengerSettings = ({ userId }) => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    facebook_linked: false,
    notification_frequency: 'daily',
    notification_detail: 'normal',
    notification_time: '10:00'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [facebookPSID, setFacebookPSID] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [userId]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/notification-settings/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setSettings({
          facebook_linked: data.facebook_linked || false,
          notification_frequency: data.notification_frequency || 'daily',
          notification_detail: data.notification_detail || 'normal',
          notification_time: data.notification_time || '10:00'
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Lỗi",
        description: "Không thể tải cài đặt thông báo",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (field, value) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/notification-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          [field]: value
        })
      });

      if (response.ok) {
        setSettings(prev => ({ ...prev, [field]: value }));
        toast({
          title: "Đã lưu",
          description: "Cài đặt thông báo đã được cập nhật"
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: "Lỗi",
        description: "Không thể lưu cài đặt",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLinkFacebook = async () => {
    if (!facebookPSID.trim()) {
      toast({
        title: "Lỗi",
        description: "Vui lòng nhập Facebook PSID",
        variant: "destructive"
      });
      return;
    }

    setIsLinking(true);
    try {
      const response = await fetch(`${API_URL}/link-facebook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          facebook_psid: facebookPSID.trim()
        })
      });

      if (response.ok) {
        setSettings(prev => ({ ...prev, facebook_linked: true }));
        setFacebookPSID('');
        toast({
          title: "Thành công",
          description: "Đã liên kết tài khoản Facebook thành công!"
        });
        fetchSettings();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to link');
      }
    } catch (error) {
      console.error('Error linking Facebook:', error);
      toast({
        title: "Lỗi",
        description: "Không thể liên kết tài khoản Facebook",
        variant: "destructive"
      });
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkFacebook = async () => {
    if (!window.confirm('Bạn có chắc muốn hủy liên kết tài khoản Facebook?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/unlink-facebook/${userId}`, {
        method: 'POST'
      });

      if (response.ok) {
        setSettings(prev => ({ ...prev, facebook_linked: false }));
        toast({
          title: "Đã hủy liên kết",
          description: "Tài khoản Facebook đã được hủy liên kết"
        });
      } else {
        throw new Error('Failed to unlink');
      }
    } catch (error) {
      console.error('Error unlinking Facebook:', error);
      toast({
        title: "Lỗi",
        description: "Không thể hủy liên kết",
        variant: "destructive"
      });
    }
  };

  const sendTestNotification = async () => {
    try {
      const response = await fetch(`${API_URL}/send-test-notification/${userId}`, {
        method: 'POST'
      });

      if (response.ok) {
        toast({
          title: "Đã gửi",
          description: "Thông báo thử nghiệm đã được gửi qua Facebook Messenger!"
        });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast({
        title: "Lỗi",
        description: error.message || "Không thể gửi thông báo",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-gray-500">Đang tải...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Facebook Account Linking */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Facebook className="h-5 w-5 text-blue-600" />
            Kết nối Facebook Messenger
          </CardTitle>
          <CardDescription>
            Liên kết tài khoản Facebook để nhận thông báo qua Messenger
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.facebook_linked ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600">
                <MessageCircle className="h-5 w-5" />
                <span className="font-medium">Đã kết nối Facebook Messenger</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={sendTestNotification}
                  className="flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Gửi thông báo thử
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleUnlinkFacebook}
                  className="flex items-center gap-2"
                >
                  <Unlink className="h-4 w-4" />
                  Hủy kết nối
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Hướng dẫn lấy Facebook PSID:</strong>
                </p>
                <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
                  <li>Mở Facebook Messenger</li>
                  <li>Nhắn tin cho Page của An Nhi</li>
                  <li>Truy cập: <code className="bg-blue-100 px-1 rounded">m.me/?show_id=true</code></li>
                  <li>Hoặc nhắn tin bất kỳ, PSID sẽ được gửi cho bạn</li>
                </ol>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Nhập Facebook PSID của bạn"
                  value={facebookPSID}
                  onChange={(e) => setFacebookPSID(e.target.value)}
                />
                <Button
                  onClick={handleLinkFacebook}
                  disabled={isLinking}
                  className="whitespace-nowrap"
                >
                  {isLinking ? 'Đang kết nối...' : 'Kết nối'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-pink-500" />
            Cài đặt thông báo
          </CardTitle>
          <CardDescription>
            Tùy chỉnh cách bạn nhận thông báo hàng ngày
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Notification Frequency */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Tần suất thông báo
            </Label>
            <Select
              value={settings.notification_frequency}
              onValueChange={(value) => updateSettings('frequency', value)}
              disabled={!settings.facebook_linked || isSaving}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Hàng ngày</SelectItem>
                <SelectItem value="weekly">Hàng tuần</SelectItem>
                <SelectItem value="off">Tắt thông báo</SelectItem>
              </SelectContent>
            </Select>
            {!settings.facebook_linked && (
              <p className="text-sm text-gray-500">
                Kết nối Facebook để bật tính năng này
              </p>
            )}
          </div>

          {/* Detail Level */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Mức độ chi tiết
            </Label>
            <Select
              value={settings.notification_detail}
              onValueChange={(value) => updateSettings('detail_level', value)}
              disabled={!settings.facebook_linked || isSaving}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="brief">Ngắn gọn - Tóm tắt nhanh</SelectItem>
                <SelectItem value="normal">Bình thường - Đủ thông tin</SelectItem>
                <SelectItem value="detailed">Chi tiết - Nhiều hướng dẫn</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notification Time */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Giờ nhận thông báo
            </Label>
            <Input
              type="time"
              value={settings.notification_time}
              onChange={(e) => updateSettings('notification_time', e.target.value)}
              disabled={!settings.facebook_linked || isSaving}
              className="max-w-[200px]"
            />
            <p className="text-sm text-gray-500">
              Thông báo sẽ được gửi vào giờ này mỗi ngày
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Notification Content Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Nội dung thông báo</CardTitle>
          <CardDescription>
            Bạn sẽ nhận được những nội dung sau
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg">
              <span className="text-xl">🥗</span>
              <div>
                <p className="font-medium text-pink-700">Dinh dưỡng</p>
                <p className="text-sm text-gray-600">
                  Gợi ý món ăn, thực phẩm tốt cho tuần mang thai của bạn
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
              <span className="text-xl">⚠️</span>
              <div>
                <p className="font-medium text-orange-700">Cảnh báo sức khỏe</p>
                <p className="text-sm text-gray-600">
                  Dấu hiệu cần chú ý trong từng giai đoạn thai kỳ
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <span className="text-xl">📅</span>
              <div>
                <p className="font-medium text-blue-700">Nhắc nhở lịch hẹn</p>
                <p className="text-sm text-gray-600">
                  Thông báo trước các lịch khám thai sắp tới
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MessengerSettings;